import { useRef, useEffect, useCallback, useMemo } from 'react';

// ============================================================================
// HELPER: Generar descripción HTML para una sede
// Se memoiza fuera del componente para evitar recalcular
// ============================================================================
const getSedeTipo = (tipo) => {
  const tiposMap = {
    cfcrl: 'OFICINA ESTATAL DEL CENTRO FEDERAL DE CONCILIACIÓN Y REGISTRO LABORAL',
    conciliacion: 'CENTRO DE CONCILIACIÓN ESTATAL',
    tribfed: 'TRIBUNALES LABORALES FEDERALES',
  };
  return tiposMap[tipo] || 'TRIBUNALES LABORALES ESTATALES';
};

const generateLocationHTML = (sedesArray) => {
  const sedesHTML = sedesArray
    .map(sede => {
      const tipo = getSedeTipo(sede.tipo);
      return `<div>
                <h6 class='title-card'>${tipo}</h6>
                <div><strong>Sede: </strong>${sede.sede}</div>
                <div><strong>Domicilio: </strong>${sede.domicilio}</div>
                ${sede.tel ? `<div><strong>Teléfono: </strong>${sede.tel}</div>` : ''}
              </div>`;
    })
    .join('');

  return `<div class='location-card'>
            <h5 class='text-center subtitle-card'>${sedesArray[0].name}</h5>
            <div class='scrollable-content'>
              ${sedesHTML}
            </div>
          </div>`;
};

const Map = ({ setCurrentState, statesData, sedes, filter, setPopup }) => {
  const mapRef = useRef();

  const getStage = useCallback(
    id => {
      const state = statesData.find(state => id === state.code);
      return state ? state.etapa : null;
    },
    [statesData]
  );

  // ========================================================================
  // OPTIMIZACIÓN 1: Memoizar la generación de locations
  // Solo se recalcula si 'sedes' cambia, no en cada render
  // ========================================================================
  const memoizedLocations = useMemo(() => {
    if (!sedes) return {};
    
    const locations = {};
    for (let municipioId in sedes) {
      const sedesArray = sedes[municipioId];
      locations[municipioId] = {
        name: sedesArray[0].name,
        lat: sedesArray[0].Municipio.lat,
        lng: sedesArray[0].Municipio.lon,
        display: 'state',
        description: generateLocationHTML(sedesArray),
      };
    }
    return locations;
  }, [sedes]);

  // ========================================================================
  // OPTIMIZACIÓN 2: Función separada para actualizar colores de estados
  // Se puede reutilizar en múltiples lugares sin regenerar locations
  // ========================================================================
  const updateStateColors = useCallback(() => {
    const { state_specific } = window.simplemaps_countrymap_mapdata;
    for (let state in state_specific) {
      const etapa = getStage(state);
      state_specific[state].color = '#e3e3e3';
      state_specific[state].popup = 'off';
      if (etapa === 1 && filter.primera) {
        state_specific[state].color = '#235b4e';
      } else if (etapa === 2 && filter.segunda) {
        state_specific[state].color = '#bc955c';
      } else if (etapa === 3 && filter.tercera) {
        state_specific[state].color = '#ddc9a3';
      }
    }
  }, [filter, getStage]);

  // Inicializar el mapa una sola vez
  useEffect(() => {
    window.simplemaps_countrymap.load(mapRef);
    const { main_settings } = window.simplemaps_countrymap_mapdata;
    main_settings.state_hover_color = '#404041';
  }, []);

  // ========================================================================
  // OPTIMIZACIÓN 3: Separar actualización de locations + colores
  // Solo se ejecuta cuando locations o filter cambian
  // ========================================================================
  useEffect(() => {
    if (!Object.keys(memoizedLocations).length) return;
    
    window.simplemaps_countrymap_mapdata.locations = memoizedLocations;
    updateStateColors();
    window.simplemaps_countrymap.refresh();
  }, [memoizedLocations, updateStateColors]);

  const handleClick = useCallback(e => {
    const classList = e.target.classList;
    const classItem = classList.item && classList.item(0);

    if (e.target.nodeName === 'path' && classItem?.startsWith('sm_state_')) {
      const currentState = classItem.replace('sm_state_', '');
      const { state_specific } = window.simplemaps_countrymap_mapdata;
      const { state_zoom, refresh_state } = window.simplemaps_countrymap;

      // Actualizar colores de todos los estados
      updateStateColors();

      // Resaltar estado seleccionado
      for (let state in state_specific) {
        refresh_state(state);
      }
      state_specific[currentState].color = '#404041';
      refresh_state(currentState);
      state_zoom(currentState);
      setCurrentState(currentState);
    } else if (e.target.nodeName === 'path' && classItem?.startsWith('sm_location_')) {
      setPopup(true);
    } else if (e.target.hasAttribute('alt') && e.target.alt === 'Close') {
      setPopup(false);
    }
  }, [updateStateColors, setCurrentState, setPopup]);

  return (
    <>
      <div id='map' ref={mapRef} onClick={handleClick} onTouchStart={handleClick}></div>
      <svg height='0' width='0' xmlns='http://www.w3.org/2000/svg'>
        <filter id='drop-shadow'>
          <feGaussianBlur in='SourceAlpha' stdDeviation='4' />
          <feOffset dx='-10' dy='10' result='offsetblur' />
          <feFlood floodColor='rgba(127, 152, 145, 0.45)' />
          <feComposite in2='offsetblur' operator='in' />
          <feMerge>
            <feMergeNode />
            <feMergeNode in='SourceGraphic' />
          </feMerge>
        </filter>
      </svg>
    </>
  );
};

export default Map;
